import type { CSSProperties, ReactNode } from "react";
import type { LatLngBoundsExpression, LatLngExpression } from "leaflet";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  OPEN_STREET_MAP_ATTRIBUTION,
  OPEN_STREET_MAP_TILE_URL,
  ensureLeafletDefaults,
} from "./leafletDefaults";

ensureLeafletDefaults();

export interface MapInitialViewport {
  center: LatLngExpression;
  zoom: number;
}

export type MapSurfaceMode = "interactive" | "readonly";

export interface MapSurfaceProps {
  initialViewport: MapInitialViewport;
  mode?: MapSurfaceMode;
  minZoom?: number;
  maxZoom?: number;
  maxBounds?: LatLngBoundsExpression;
  maxBoundsViscosity?: number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/**
 * Sole owner of the Leaflet `MapContainer`: shared CSS, OSM tiles and
 * attribution, the default marker icon, and the initial viewport. Features own
 * their data layers and pass viewport/boundary/popup controllers as children.
 */
export const MapSurface = ({
  initialViewport,
  mode = "interactive",
  minZoom,
  maxZoom,
  maxBounds,
  maxBoundsViscosity,
  className,
  style,
  children,
}: MapSurfaceProps) => {
  const isReadonly = mode === "readonly";

  return (
    <MapContainer
      center={initialViewport.center}
      zoom={initialViewport.zoom}
      style={style ?? { height: "100%", width: "100%" }}
      className={className}
      minZoom={minZoom}
      maxZoom={maxZoom}
      maxBounds={maxBounds}
      maxBoundsViscosity={maxBoundsViscosity}
      zoomAnimation={false}
      zoomControl={!isReadonly}
      dragging={!isReadonly}
      scrollWheelZoom={!isReadonly}
      doubleClickZoom={!isReadonly}
      touchZoom={!isReadonly}
      boxZoom={!isReadonly}
      keyboard={!isReadonly}
    >
      <TileLayer
        attribution={OPEN_STREET_MAP_ATTRIBUTION}
        url={OPEN_STREET_MAP_TILE_URL}
      />
      {children}
    </MapContainer>
  );
};

export default MapSurface;
